import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import ImageGenerator from '../components/ImageGenerator';
import axios from 'axios';
import { marketingTemplates, getTemplatesByCategory, fillTemplate } from '../data/marketingTemplates';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface MarketingContent {
  salesPage: {
    headline: string;
    subheadline: string;
    bulletPoints: string[];
    cta: string;
  };
  email: {
    subjectLines: string[];
  };
  socialMedia: Array<{
    platform: string;
    hook: string;
    post: string;
  }>;
}

const MarketingGenerator: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [marketingContent, setMarketingContent] = useState<MarketingContent | null>(null);

  useEffect(() => {
    fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundCourse = response.data.courses.find((c: any) => c.id === courseId);
      if (foundCourse) {
        setCourse(foundCourse);
      } else {
        toast.error('Course not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const generateMarketing = async () => {
    if (!course) return;

    // Free tier users should not reach here, but just in case
    if (user?.tier === 'free') {
      toast.error('Please upgrade to Basic or Premium to use AI generation');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-marketing`,
        {
          courseTitle: course.title,
          courseDescription: course.description,
          targetAudience: course.targetAudience || ''
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMarketingContent(response.data.marketingContent);
      toast.success('Marketing content generated successfully!');
    } catch (error: any) {
      console.error('Error generating marketing content:', error);
      if (error.response?.data?.requiresUpgrade) {
        toast.error(error.response.data.error);
        // Optionally redirect to pricing page
      } else {
        toast.error(error.response?.data?.error || 'Failed to generate marketing content');
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/course/${courseId}`)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Course
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">{user?.name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="bg-gradient-to-br from-pink-600 to-purple-600 text-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-4xl font-bold mb-4">📧 Marketing Content Generator</h2>
              <p className="text-purple-100 text-lg mb-2">
                Generate high-converting marketing materials for: <strong>{course?.title}</strong>
              </p>
              <p className="text-purple-200">
                Get ready-to-use sales pages, email campaigns, and social media posts powered by AI!
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/social-calendar/${courseId}`)}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg font-bold hover:shadow-xl transition text-sm"
              >
                📅 Social Calendar
              </button>
              <button
                onClick={() => navigate(`/email-sequences/${courseId}`)}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg font-bold hover:shadow-xl transition text-sm"
              >
                📧 Email Sequences
              </button>
            </div>
          </div>
        </div>

        {/* Generate Button or Templates (based on tier) */}
        {!marketingContent && user?.tier === 'free' && (
          <div className="space-y-8">
            {/* Upgrade Banner for Free Users */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl shadow-lg p-8 text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                AI Marketing Generation is a Premium Feature
              </h3>
              <p className="text-gray-700 mb-6 text-lg max-w-2xl mx-auto">
                Get instant, AI-powered marketing content including sales pages, email campaigns, and social media posts. Check out these basic templates, then upgrade to unlock AI generation!
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transform hover:scale-105 transition"
              >
                ⚡ Upgrade to Basic - $29/mo
              </button>
            </div>

            {/* Basic Templates for Free Users */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
                <h3 className="text-2xl font-bold">📋 Basic Marketing Templates</h3>
                <p className="text-purple-100">Use these templates to get started - upgrade for AI-powered customization!</p>
              </div>
              <div className="p-6 space-y-8">
                {/* Sales Page Templates */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📄</span>
                    Sales Page Copy
                  </h4>
                  <div className="space-y-4">
                    {getTemplatesByCategory('salesPage').map(template => (
                      <div key={template.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-sm font-semibold text-purple-600">{template.name}</p>
                          <button
                            onClick={() => copyToClipboard(template.template)}
                            className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <p className="text-gray-700 font-mono text-sm bg-white p-3 rounded">{template.template}</p>
                        <p className="text-xs text-gray-500 mt-2">Variables: {template.variables.join(', ')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Email Templates */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📧</span>
                    Email Subject Lines
                  </h4>
                  <div className="space-y-4">
                    {getTemplatesByCategory('email').map(template => (
                      <div key={template.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-sm font-semibold text-blue-600">{template.name}</p>
                          <button
                            onClick={() => copyToClipboard(template.template)}
                            className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <p className="text-gray-700 font-mono text-sm bg-white p-3 rounded">{template.template}</p>
                        <p className="text-xs text-gray-500 mt-2">Variables: {template.variables.join(', ')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social Media Templates */}
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📱</span>
                    Social Media Posts
                  </h4>
                  <div className="space-y-4">
                    {getTemplatesByCategory('social').map(template => (
                      <div key={template.id} className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-sm font-semibold text-pink-600">{template.name}</p>
                            <p className="text-xs text-gray-600">{template.platform}</p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(template.template)}
                            className="text-pink-600 hover:text-pink-700 font-semibold text-sm"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <p className="text-gray-700 font-mono text-sm bg-white p-3 rounded whitespace-pre-wrap">{template.template}</p>
                        <p className="text-xs text-gray-500 mt-2">Variables: {template.variables.join(', ')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upgrade CTA */}
                <div className="bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-400 rounded-lg p-6 text-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Want AI to Do This For You?</h4>
                  <p className="text-gray-700 mb-4">
                    Upgrade to Basic and get AI-generated marketing content customized for YOUR course - instantly!
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition"
                  >
                    See Pricing Plans →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Generation for Basic/Premium Users */}
        {!marketingContent && user?.tier !== 'free' && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200 mb-8">
            <div className="max-w-2xl mx-auto">
              <div className="text-6xl mb-6">🚀</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Create Your Marketing Materials?
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Our AI will generate a complete marketing package including:
              </p>
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-3xl mb-2">📄</div>
                  <div className="font-semibold text-gray-900">Sales Page</div>
                  <div className="text-sm text-gray-600">Headlines, bullets, CTA</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-3xl mb-2">📧</div>
                  <div className="font-semibold text-gray-900">Email Campaign</div>
                  <div className="text-sm text-gray-600">Subject lines & sequences</div>
                </div>
                <div className="bg-pink-50 rounded-lg p-4">
                  <div className="text-3xl mb-2">📱</div>
                  <div className="font-semibold text-gray-900">Social Media</div>
                  <div className="text-sm text-gray-600">Posts for all platforms</div>
                </div>
              </div>
              <button
                onClick={generateMarketing}
                disabled={generating}
                className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transform hover:scale-105 transition disabled:opacity-50"
              >
                {generating ? 'Generating Marketing Content...' : '✨ Generate Marketing Package'}
              </button>
            </div>
          </div>
        )}

        {/* Marketing Content Display */}
        {marketingContent && (
          <div className="space-y-8">
            {/* Sales Page Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
                <h3 className="text-2xl font-bold">📄 Sales Page Content</h3>
                <p className="text-purple-100">Copy-paste ready sales page elements</p>
              </div>
              <div className="p-6 space-y-6">
                {/* Headline */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-bold text-gray-900">Headline:</h4>
                    <button
                      onClick={() => copyToClipboard(marketingContent.salesPage.headline)}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
                    <p className="text-2xl font-bold text-gray-900">
                      {marketingContent.salesPage.headline}
                    </p>
                  </div>
                </div>

                {/* Subheadline */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-bold text-gray-900">Subheadline:</h4>
                    <button
                      onClick={() => copyToClipboard(marketingContent.salesPage.subheadline)}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-lg text-gray-700">
                      {marketingContent.salesPage.subheadline}
                    </p>
                  </div>
                </div>

                {/* Bullet Points */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-bold text-gray-900">Key Benefits:</h4>
                    <button
                      onClick={() => copyToClipboard(marketingContent.salesPage.bulletPoints.join('\n'))}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                    >
                      📋 Copy All
                    </button>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    {marketingContent.salesPage.bulletPoints.map((bullet, idx) => (
                      <div key={idx} className="flex items-start">
                        <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-bold text-gray-900">Call-to-Action:</h4>
                    <button
                      onClick={() => copyToClipboard(marketingContent.salesPage.cta)}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 text-center">
                    <p className="text-xl font-bold text-green-900">
                      {marketingContent.salesPage.cta}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Campaign Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
                <h3 className="text-2xl font-bold">📧 Email Campaign</h3>
                <p className="text-blue-100">High-converting email subject lines</p>
              </div>
              <div className="p-6 space-y-4">
                {marketingContent.email.subjectLines.map((subject, idx) => (
                  <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-center">
                    <div className="flex-1">
                      <span className="text-sm text-blue-600 font-semibold mr-3">Email {idx + 1}:</span>
                      <span className="text-gray-900 font-medium">{subject}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(subject)}
                      className="text-blue-600 hover:text-blue-700 font-semibold text-sm ml-4"
                    >
                      📋 Copy
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-pink-600 to-rose-600 text-white p-6">
                <h3 className="text-2xl font-bold">📱 Social Media Posts</h3>
                <p className="text-pink-100">Ready-to-post content for every platform</p>
              </div>
              <div className="p-6 space-y-6">
                {marketingContent.socialMedia.map((social, idx) => (
                  <div key={idx} className="bg-pink-50 border border-pink-200 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-bold text-gray-900">
                        {social.platform}
                      </h4>
                      <button
                        onClick={() => copyToClipboard(`${social.hook}\n\n${social.post}`)}
                        className="text-pink-600 hover:text-pink-700 font-semibold text-sm"
                      >
                        📋 Copy
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-pink-600 mb-1">Hook:</p>
                        <p className="text-gray-900 font-medium">{social.hook}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-pink-600 mb-1">Post:</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{social.post}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Marketing Images Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
                <h3 className="text-2xl font-bold">🎨 Marketing Images</h3>
                <p className="text-purple-100">Generate AI images for your social posts and ads</p>
              </div>
              <div className="p-6">
                <ImageGenerator
                  courseTitle={course?.title || 'Course'}
                  defaultPrompt={`Professional marketing image for "${course?.title}" online course. Show someone learning, achieving success, or the transformation your course provides.`}
                  platform="instagram"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex flex-wrap gap-4 justify-center">
                <button
                  onClick={generateMarketing}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition"
                >
                  🔄 Regenerate Content
                </button>
                <button
                  onClick={() => navigate(`/course/${courseId}`)}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:border-gray-400 transition"
                >
                  ← Back to Course
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MarketingGenerator;
